(() => {
  const requests = new Map();
  const unique = xs => [...new Set(xs)].sort((a,b) => String(a).localeCompare(String(b),undefined,{numeric:true}));
  const chassisCodes = value => String(value || '').toUpperCase().replace(/FG[ -]X/g,'FGX').split(/[|/]/).map(x=>x.trim()).filter(Boolean);
  const applicationMatches = (a,s) => a[0] === s.make && a[1] === s.model &&
    (s.chassis === 'all' || !a[4].length || chassisCodes(s.chassis).some(code=>a[4].includes(code)));
  const yearsFor = vehicle => vehicle.years.filter(year=>Number.isInteger(year)&&year<=new Date().getFullYear());
  const chassisFor = (vehicle,year) => vehicle.chassis.filter(code=>year==='all'||!vehicle.chassisYears[code]||(+year>=vehicle.chassisYears[code][0]&&+year<=vehicle.chassisYears[code][1]));
  const productPriority = p => {
    const identity=`${p.vendor} ${p.title} ${p.handle}`;
    if(/\braceworks\b/i.test(identity))return 5;
    if(p.vendor==='Hatrick Racing Products')return 0;
    if(/\bkoyo(?:rad)?\b/i.test(p.vendor)&&/\bradiator\b/i.test(p.title)&&!/\bcap\b/i.test(p.title))return 1;
    if(p.vendor==='Link Engine Management'&&((/\bECU\b/i.test(p.title)&&/\bG[345]X?\b/i.test(p.title))||['link-ecu-gtrlink-ngtrx-bundle','link-ecu-fzj100-link-tfzj100x'].includes(p.handle)))return 2;
    if(/\bradium\b/i.test(identity))return 3;
    return 4;
  };
  const radiumGroup = p => {
    if(/fuel rail/i.test(p.title))return 'radiumFuelRails';
    if(/catch can|air[ -]oil separator|\bAOS(?:-R)?\b|\bPCV\b|\bCCV\b/i.test(p.title))return 'radiumCatchCans';
    if(/fuel (?:pump|hanger|surge|cell|tank)|\bFHST\b|pump (?:add-on|collector)/i.test(p.title))return 'radiumPumpsTanks';
    if(/fuel filter|fuel pressure|fuel pulse|\bFP[RD](?:-\w+)?\b/i.test(p.title))return 'radiumFiltersPressure';
    if(/fitting|adapter|adaptor|hose|fuel (?:feed|line)|retainer|connector|harness|wiring/i.test(p.title))return 'radiumFittingsLines';
    return 'radiumAccessories';
  };
  const radiumGroups=['radiumFuelRails','radiumCatchCans','radiumPumpsTanks','radiumFiltersPressure','radiumFittingsLines','radiumAccessories'];
  const matchingProducts = (products,s) => {
    const matched=products.filter(p => p.applications.some(a => applicationMatches(a,s)));
    const grouped=matched.filter(p=>productPriority(p)===3).length>=10;
    return matched.sort((a,b)=>productPriority(a)-productPriority(b)||(productPriority(a)===0?Number(!/\bhose kit\b/i.test(a.title))-Number(!/\bhose kit\b/i.test(b.title)):0)||(grouped&&productPriority(a)===3?radiumGroups.indexOf(radiumGroup(a))-radiumGroups.indexOf(radiumGroup(b)):0));
  };
  const resultSection = (p,grouped) => !grouped?null:productPriority(p)===3?radiumGroup(p):productPriority(p)===4?'otherParts':productPriority(p)===5?'raceworksParts':null;
  if (typeof module !== 'undefined' && module.exports) module.exports = {applicationMatches,matchingProducts,yearsFor,chassisFor,productPriority,radiumGroup,resultSection};
  if (typeof document === 'undefined') return;
  const fetchJSON = url => {
    if (!requests.has(url)) requests.set(url,(async () => {
      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(),20000);
      try {
        const response = await fetch(url,{credentials:'same-origin',signal:controller.signal});
        if (!response.ok) throw new Error('Request failed');
        return await response.json();
      } finally {clearTimeout(timer);}
    })().catch(error => {requests.delete(url);throw error;}));
    return requests.get(url);
  };
  const init = root => {
    if (root.dataset.vfReady) return;
    root.dataset.vfReady = 'true';
    const t = JSON.parse(root.querySelector('[data-vf-translations]').textContent);
    const form = root.querySelector('form');
    const fields = Object.fromEntries(['make','model','year','chassis'].map(k=>[k,form.elements['vf_'+k]]));
    const submit = form.querySelector('[type="submit"]');
    const status = root.querySelector('.jdm-vf__status');
    let products,applications,vehicles,modelAliases={},loading,run=0,resultObserver=null;
    const cancelResults = () => {++run;resultObserver?.disconnect();resultObserver=null;};
    const results = document.createElement('div');
    results.className='jdm-vf-results';results.hidden=true;results.setAttribute('aria-live','polite');
    root.querySelector('.jdm-vf__inner').append(results);
    const selection = () => Object.fromEntries(Object.entries(fields).map(([k,f])=>[k,f.value]));
    const options = (select,values,placeholder) => {
      select.replaceChildren(new Option(placeholder,''));
      values.forEach(v=>select.add(new Option(v.label??v,v.value??v)));
      select.disabled=!values.length;
    };
    const ready = () => {submit.disabled=Object.values(fields).some(f=>!f.value);form.elements.q.value='';};
    const vehicleRows = () => vehicles.filter(v=>v.make===fields.make.value&&v.model===fields.model.value);
    const updateChassis = () => {
      const values=unique(vehicleRows().flatMap(v=>chassisFor(v,fields.year.value)));
      const groups=fields.make.value==='Ford'&&fields.model.value==='Falcon'&&values.some(v=>v==='BA'||v==='BF')?[{value:'BA/BF',label:'BA / BF'}]:[];
      options(fields.chassis,[{value:'all',label:t.allChassis},...groups,...values],t.chooseChassis);
      fields.chassis.value=values.length===1?values[0]:'all';ready();
    };
    const updateYears = () => {
      const years=vehicleRows().flatMap(v=>yearsFor(v).map(String));
      options(fields.year,[{value:'all',label:t.allYears},...unique(years).reverse()],t.chooseYear);
      fields.year.value='all';updateChassis();
    };
    const updateModels = () => {
      options(fields.model,unique(vehicles.filter(v=>v.make===fields.make.value).map(v=>v.model)),t.chooseModel);
      options(fields.year,[],t.chooseYear);options(fields.chassis,[],t.chooseChassis);ready();
    };
    const element = (tag,text,className) => {const el=document.createElement(tag);if(text)el.textContent=text;if(className)el.className=className;return el;};
    const productLink = handle => {
      const url=new URL((window.Shopify?.routes?.root||'/')+'products/'+encodeURIComponent(handle),location.origin);
      const preview=new URL(location.href).searchParams.get('preview_theme_id');if(preview)url.searchParams.set('preview_theme_id',preview);return url;
    };
    const showResults = async () => {
      if(!products||submit.disabled)return;
      cancelResults();
      const token=run,s=selection(),matches=matchingProducts(products,s),size=12;
      results.hidden=false;results.replaceChildren();
      const heading=element('h3',t.resultsHeading);heading.tabIndex=-1;results.append(heading);
      results.append(element('p',`${s.make} ${s.model} · ${s.year==='all'?t.allYears:s.year} · ${s.chassis==='all'?t.allChassis:s.chassis}`));
      const count=element('p',t.resultsCount.replace('__COUNT__',String(matches.length)));results.append(count);
      if(location.pathname.includes('/search'))document.querySelectorAll('[id$="__search-template"]').forEach(el=>el.hidden=true);
      if(!matches.length){results.append(element('p',t.noMatches));heading.focus({preventScroll:true});return;}
      const grid=element('div','', 'jdm-vf-results__grid');results.append(grid);
      let offset=0,busy=false,lastSection=null;
      const groupedRadium=matches.filter(p=>productPriority(p)===3).length>=10;
      const sentinel=element('div','', 'jdm-vf-results__pagination');
      sentinel.style.minHeight='1px';
      const loadStatus=element('p');loadStatus.setAttribute('role','status');
      const more=element('button',t.loadMore);more.type='button';
      const automatic='IntersectionObserver' in window;
      more.hidden=automatic;
      sentinel.append(loadStatus,more);results.append(sentinel);
      const appendBatch=async()=>{
        if(busy||token!==run||offset>=matches.length||!root.isConnected)return;
        busy=true;resultObserver?.unobserve(sentinel);more.disabled=true;
        loadStatus.textContent=t.loading;grid.setAttribute('aria-busy','true');
        let cursor=0;const entries=matches.slice(offset,offset+size);offset+=entries.length;
      const cards=entries.map(p=>{
        const section=resultSection(p,groupedRadium);
        if(section&&section!==lastSection){grid.append(element('h4',t[section],'jdm-vf-results__group'));lastSection=section;}
        const card=element('article','', 'jdm-vf-results__card');
        const title=element('a',p.title);title.href=productLink(p.handle).href;
        const vendor=element('p',p.vendor,'jdm-vf-results__vendor');
        const info=element('p',t.loading);card.append(title,vendor,info);grid.append(card);return {p,card,title,info};
      });
      const worker=async()=>{while(cursor<cards.length){const {p,card,title,info}=cards[cursor++];try{
        const url=productLink(p.handle);url.pathname+='.js';
        const live=await fetchJSON(url.href);if(token!==run)return;
        if(!live||!live.title||!Array.isArray(live.variants))throw new Error('Unavailable');
        title.textContent=live.title;
        if(live.featured_image){const link=element('a');link.href=title.href;link.tabIndex=-1;link.setAttribute('aria-hidden','true');const img=element('img');img.src=live.featured_image;img.alt='';img.loading='lazy';img.width=320;img.height=240;link.append(img);card.prepend(link);}
        const currency=window.Shopify?.currency?.active||'AUD';
        info.textContent=new Intl.NumberFormat(document.documentElement.lang||'en-AU',{style:'currency',currency}).format(live.price_min/100)+(live.available?'':` · ${t.outOfStock}`);
        const view=element('a',t.viewProduct,'jdm-vf-results__view');view.href=title.href;card.append(view);
      }catch{if(token===run){info.textContent=t.productUnavailable;card.remove();}}}};
      await Promise.all(Array.from({length:Math.min(4,cards.length)},worker));
        if(token!==run)return;
        busy=false;more.disabled=false;loadStatus.textContent='';grid.setAttribute('aria-busy','false');
        if(offset>=matches.length){resultObserver?.disconnect();sentinel.remove();}
        else resultObserver?.observe(sentinel);
      };
      more.addEventListener('click',()=>void appendBatch());
      if(automatic)resultObserver=new IntersectionObserver(entries=>{
        if(entries.some(entry=>entry.isIntersecting))void appendBatch();
      },{rootMargin:'400px 0px'});
      await appendBatch();if(token!==run)return;
      heading.focus({preventScroll:true});
      if(location.pathname.includes('/search'))document.querySelectorAll('[id$="__search-template"]').forEach(el=>el.hidden=true);
    };
    const saveAndSearch = async () => {
      ready();if(submit.disabled)return;const s=selection();
      try{sessionStorage.setItem('jdmVehicle',JSON.stringify(s));}catch{}
      const url=new URL(location.href);['q','type','vf_engine','vf_application','vf_incomplete'].forEach(k=>url.searchParams.delete(k));
      Object.entries(s).forEach(([k,v])=>url.searchParams.set('vf_'+k,v));
      url.hash='find-your-vehicle';history.pushState(null,'',url);await showResults();
    };
    const restore = async () => {
      const url=new URL(location.href);let saved;
      if(url.searchParams.has('vf_make'))saved=Object.fromEntries(['make','model','year','chassis'].map(k=>[k,url.searchParams.get('vf_'+k)||'all']));
      else try{saved=JSON.parse(sessionStorage.getItem('jdmVehicle')||'null');}catch{}
      if(!saved)return;const alias=modelAliases[saved.make]?.[saved.model];if(alias){saved.model=alias.model;if(saved.chassis==='all'&&alias.chassis.length===1)saved.chassis=alias.chassis[0];}fields.make.value=saved.make;if(!fields.make.value)return;
      updateModels();fields.model.value=saved.make==='Ford'&&/^Falcon(?: XR[68].*)?$/.test(saved.model)?'Falcon':saved.model;if(!fields.model.value)return;
      updateYears();if([...fields.year.options].some(o=>o.value===saved.year))fields.year.value=saved.year;
      updateChassis();if([...fields.chassis.options].some(o=>o.value===saved.chassis))fields.chassis.value=saved.chassis;
      ready();if(url.searchParams.has('vf_make')||root.dataset.legacyResults==='true')await showResults();
    };
    const load = async () => {
      if(products)return products;if(loading)return loading;status.textContent=t.loading;fields.make.disabled=true;
      loading=(async()=>{try{
        let data=await fetchJSON(root.dataset.fitment);
        if(!Array.isArray(data.vehicles))throw new Error('Invalid vehicle metadata');
        vehicles=data.vehicles;modelAliases=data.modelAliases||{};
        if(Array.isArray(data.shards)){
          const base=new URL(root.dataset.fitment,location.href);
          const parts=await Promise.all(data.shards.map(name=>fetchJSON(new URL(name,base).href)));
          if(parts.some(part=>!Array.isArray(part.products)))throw new Error('Invalid fitment data');
          data={products:parts.flatMap(part=>part.products)};
        }
        if(!Array.isArray(data.products))throw new Error('Invalid data');
        products=data.products;applications=products.flatMap(p=>p.applications);
        options(fields.make,unique(vehicles.map(v=>v.make)),t.chooseMake);status.textContent='';await restore();return products;
      }catch{loading=null;fields.make.disabled=false;status.textContent=t.loadError;return null;}})();return loading;
    };
    Object.values(fields).forEach(field=>field.addEventListener('change',()=>{cancelResults();results.hidden=true;}));
    fields.make.addEventListener('focus',load);form.addEventListener('pointerdown',()=>void load(),{passive:true});
    fields.make.addEventListener('change',async()=>{await load();if(products)updateModels();});
    fields.model.addEventListener('change',updateYears);fields.year.addEventListener('change',updateChassis);fields.chassis.addEventListener('change',ready);
    form.addEventListener('submit',async e=>{e.preventDefault();await load();if(products&&form.reportValidity())await saveAndSearch();});
    root.querySelector('.jdm-vf__reset').addEventListener('click',()=>{cancelResults();fields.make.value='';if(products)updateModels();results.hidden=true;results.replaceChildren();status.textContent='';try{sessionStorage.removeItem('jdmVehicle');}catch{}const url=new URL(location.href);['make','model','year','chassis','engine','application','incomplete'].forEach(k=>url.searchParams.delete('vf_'+k));history.replaceState(null,'',url);});
    if(root.dataset.legacyResults==='true'||new URL(location.href).searchParams.has('vf_make'))void load();
    else if('IntersectionObserver' in window){const observer=new IntersectionObserver(entries=>{if(entries.some(e=>e.isIntersecting)){observer.disconnect();void load();}},{rootMargin:'150px'});observer.observe(root);}else void load();
    window.addEventListener('popstate',()=>{if(!new URL(location.href).searchParams.has('vf_make')){cancelResults();results.hidden=true;}else if(products)void restore();});
  };
  const start=()=>document.querySelectorAll('[data-jdm-vehicle-finder]').forEach(init);
  if(document.readyState==='loading')document.addEventListener('DOMContentLoaded',start,{once:true});else start();
  document.addEventListener('shopify:section:load',start);
})();


